import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { journalsQueries } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { NOTIFICATION_MESSAGES } from "@/shared/utils";

export const useSectionFourController = (
  sectionFourListId: string,
  sectionFourId: string,
) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: journalsQueries.getSection4({
        section4ListId: sectionFourListId,
      }).queryKey,
    });
  };

  const rejectSignEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/{itemNoVersionId}/itemPartOne/{itemPartOneId}/status/signerRejected",
  );

  const statusReturnEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/{itemNoVersionId}/itemPartOne/{itemPartOneId}/status/inputterReturn",
  );

  const returnEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/{itemNoVersionId}/itemPartOne/{itemPartOneId}/return",
  );

  const sendToSignItem = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/{itemNoVersionId}/itemPartOne/{itemPartOneId}/sendToSignItemPart",
  );

  const sendToSignItems = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/sendToSignItems",
  );

  const deleteEntry = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/section-four/items/{itemNoVersionId}",
  );

  const handleReject = ({
    itemNoVersionId,
    itemPartOneId,
    comment,
    signerEmployeeId,
  }: {
    itemNoVersionId: string;
    itemPartOneId: string;
    comment: string;
    signerEmployeeId: string;
  }) => {
    rejectSignEntry.mutate(
      {
        params: {
          path: { sectionFourListId, itemNoVersionId, itemPartOneId },
        },
        body: {
          comment,
          signer: { signerEmployeeId: signerEmployeeId || "" },
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_REJECTED_SUCCESS }); // "Запись отклонена"
          try {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId }).queryKey,
            });
          } catch (err) {
            /* noop: failed to invalidate item cache */
          }
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_REJECT_ERROR }); // "Ошибка при отклонении"
          invalidate();
        },
      },
    );
  };

  const handleStatusReturn = ({
    itemNoVersionId,
    itemPartOneId,
  }: {
    itemNoVersionId: string;
    itemPartOneId: string;
    comment: string;
  }) => {
    statusReturnEntry.mutate(
      {
        params: { path: { sectionFourListId, itemNoVersionId, itemPartOneId } },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RECALLED_FROM_SIGNING_SUCCESS }); // "Запись отозвана с подписания"
          try {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId }).queryKey,
            });
          } catch (err) {
            /* noop: failed to invalidate item cache */
          }
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RECALL_ERROR }); // "Ошибка при отзыве"
          invalidate();
        },
      },
    );
  };

  const handleReturn = ({
    itemNoVersionId,
    itemPartOneId,
  }: {
    itemNoVersionId: string;
    itemPartOneId: string;
  }) => {
    returnEntry.mutate(
      {
        params: { path: { sectionFourListId, itemNoVersionId, itemPartOneId } },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RETURNED_TO_NEW }); // "Запись возвращена в статус NEW"
          try {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId }).queryKey,
            });
          } catch (err) {
            /* noop: failed to invalidate item cache */
          }
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RETURN_ERROR }); // "Ошибка при возврате записи"
          invalidate();
        },
      },
    );
  };

  const handleSendToSign = ({
    itemNoVersionId,
    itemPartOneId,
  }: {
    itemNoVersionId: string;
    itemPartOneId: string;
  }) => {
    sendToSignItem.mutate(
      {
        params: { path: { sectionFourListId, itemNoVersionId, itemPartOneId } },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_SENT_TO_SIGNING }); // "Отправлено на подписание"
          queryClient.invalidateQueries({
            queryKey:
              journalsQueries.getSectionFourSignersList(sectionFourId)
                .queryKey,
          });
          try {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId }).queryKey,
            });
          } catch (err) {
            /* noop: failed to invalidate item cache */
          }
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_SEND_TO_SIGNING_ERROR_ALT }); // "Не удалось отправить на подписание"
          invalidate();
        },
      },
    );
  };

  const handleSendToSignBatch = (
    items: { itemNoVersionId: string; selected: boolean }[],
  ) => {
    sendToSignItems.mutate(
      {
        params: { path: { sectionFourListId } },
        body: { itemsIds: items },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_BULK_SENT_SUCCESS }); // "Групповая отправка выполнена"
          queryClient.invalidateQueries({
            queryKey:
              journalsQueries.getSectionFourSignersList(sectionFourId)
                .queryKey,
          });
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_BULK_SEND_ERROR }); // "Ошибка при групповой отправке"
          invalidate();
        },
      },
    );
  };

  const handleDelete = ({ itemNoVersionId }: { itemNoVersionId: string }) => {
    deleteEntry.mutate(
      {
        params: { path: { itemNoVersionId } },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_DELETED_SUCCESS }); // "Запись удалена"
          invalidate();
        },
        onError: (error: any) => {
          const rawStatus =
            (error?.response && (error.response.status ?? error.response?.data?.status ?? error.response?.data?.code)) ??
            error?.status ??
            error?.code;
          const isConflict = Number(rawStatus) === 409 || String(rawStatus).includes("409");
          if (isConflict) {
            notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_DELETE_RELATED_DATA_ERROR }); // "Есть связанные данные, удаление невозможно выполнить"
          } else {
            notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_DELETE_ERROR }); // "Ошибка при удалении записи"
          }
          invalidate();
        },
      },
    );
  };

  return {
    handleReject,
    handleStatusReturn,
    handleReturn,
    handleSendToSign,
    handleSendToSignBatch,
    handleDelete,
  };
};

